import React, { useState, useEffect, useCallback } from "react";
import {
  Space,
  Typography,
  Spin,
  Empty,
  Button,
  message,
  Image,
  Modal,
  Card,
  Segmented,
} from "antd";
import {
  AppstoreOutlined,
  FileOutlined,
  DownloadOutlined,
  EyeOutlined,
  CopyOutlined,
  ReloadOutlined,
  TableOutlined,
} from "@ant-design/icons";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLocalStorage } from "@rehooks/local-storage";
import { useAuth0 } from "../../../react-auth0-spa";
import config from "../../../api_config.json";
import { useAppContext } from "../../../context";
import AllegatiGrid from "./allegati/AllegatiGrid";
import AllegatiTable from "./allegati/AllegatiTable";
import { isImage } from "./allegati/allegatiUtils";

const { Title, Text } = Typography;

// La griglia mostra 5-6 card per riga: con pagine piccole il contenuto non supera
// il viewport, non si genera scroll e InfiniteScroll non chiamerebbe mai next().
const PAGE_SIZE = 48;

const VIEW_MODE_STORAGE_KEY = "orgAllegatiViewMode";

const viewOptions = [
  { label: "Griglia", value: "grid", icon: <AppstoreOutlined /> },
  { label: "Tabella", value: "table", icon: <TableOutlined /> },
];

// GraphQL query for allegati
const allegatiQuery = `
query AllegatiQuery($first: Int!, $skip: Int, $where: AllegatiWhereInput, $order: [AllegatiOrder!]) {
  allegatis(
    first: $first,
    skip: $skip,
    where: $where,
    order: $order
  ) {
    count
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
    edges {
      node {
        objectId
        nome
        mimeType
        size_bytes
        condiviso
        provino
        createdAt
        updatedAt
        allegato {
          __typename
          name
          url
        }
        defunto_pointer {
          objectId
          nome
          cognome
        }
        office_pointer {
          objectId
        }
        isAiEdit
        isAutoColor
        isBlurBg
        isChangeClothes
        isColorizeBw
        isExpand
        isImageEnhancement
        isMagicEraser
        isRemovebg
        isSmile
        isBackgroundFusion
        backgroundFusionRawBackground
        backgroundFusionRawSubject
        backgroundFusionComposite
      }
    }
  }
}`.replace(/\r?\n|\r/g, "").replace(/\t/g, " ");

const OrgAllegati = ({ orgId }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [viewMode, setViewMode] = useLocalStorage(VIEW_MODE_STORAGE_KEY, "grid");

  const { isProd } = useAppContext();
  const { getTokenSilently } = useAuth0();

  // La griglia è la vista di default: qualsiasi valore diverso da "table" ci ricade.
  const isGridView = viewMode !== "table";

  // Fetch items from the API
  const fetchItems = useCallback(async (skip = 0, isRefresh = false) => {
    if (skip === 0 || isRefresh) {
      setLoading(true);
      if (isRefresh) {
        setItems([]);
      }
    } else {
      setLoadingMore(true);
    }

    try {
      const accessToken = await getTokenSilently();

      // Filter by organization
      const variables = {
        first: PAGE_SIZE,
        skip: skip,
        where: {
          org_pointer: {
            have: {
              objectId: {
                equalTo: orgId
              }
            }
          }
        },
        order: ["createdAt_DESC"],
      };

      const response = await fetch(
        `${config.baseUrl[isProd ? "prod" : "dev"]}/graphql?raw=true`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query: allegatiQuery,
            variables,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const result = data.allegatis;
        const newItems = result.edges.map(edge => edge.node) || [];

        if (skip === 0 || isRefresh) {
          setItems(newItems);
        } else {
          setItems(prev => [...prev, ...newItems]);
        }

        setTotalCount(result.count || 0);
        setHasNextPage(result.pageInfo?.hasNextPage || false);
      } else {
        const errorText = await response.text();
        console.error("Failed to fetch allegati:", response.status, errorText);
        message.error("Errore nel caricamento degli allegati");
      }
    } catch (error) {
      console.error("Error fetching allegati:", error);
      message.error("Errore nel caricamento degli allegati");
    }

    setLoading(false);
    setLoadingMore(false);
  }, [getTokenSilently, isProd, orgId]);

  // Load items on mount
  useEffect(() => {
    fetchItems(0);
  }, [fetchItems]);

  // Handle file preview
  const handlePreview = (item) => {
    if (isImage(item.mimeType) && item.allegato?.url) {
      setPreviewImage(item.allegato.url);
      setPreviewTitle(item.nome || "Anteprima");
      setPreviewVisible(true);
    }
  };

  // Handle file actions
  const handleFileAction = (item, action) => {
    const fileUrl = item.allegato?.url;

    if (!fileUrl) {
      message.warning("URL file non disponibile");
      return;
    }

    if (action === "view") {
      if (isImage(item.mimeType)) {
        handlePreview(item);
      } else {
        window.open(fileUrl, "_blank");
      }
    } else if (action === "download") {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = item.nome || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (action === "copy") {
      navigator.clipboard.writeText(fileUrl).then(() => {
        message.success("URL copiato negli appunti!");
      }).catch(() => {
        message.error("Impossibile copiare l'URL");
      });
    }
  };

  // Load more items for infinite scroll
  const handleLoadMore = () => {
    if (hasNextPage && !loadingMore) {
      fetchItems(items.length);
    }
  };

  // Refresh the current view
  const handleRefresh = () => {
    fetchItems(0, true);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", marginTop: "50px" }}>
          <Spin size="large" />
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <Empty
          description="Nessun allegato trovato per questa organizzazione"
          style={{ marginTop: "50px" }}
        />
      );
    }

    return (
      <InfiniteScroll
        dataLength={items.length}
        next={handleLoadMore}
        hasMore={hasNextPage}
        loader={
          <div style={{ textAlign: "center", margin: "20px 0" }}>
            <Spin size="large" />
          </div>
        }
        endMessage={
          <div style={{ textAlign: "center", margin: "20px 0", color: "#999" }}>
            Non ci sono altri allegati da caricare
          </div>
        }
        style={{ overflow: "visible" }}
      >
        {isGridView ? (
          <AllegatiGrid
            items={items}
            isProd={isProd}
            onFileAction={handleFileAction}
          />
        ) : (
          <AllegatiTable
            items={items}
            isProd={isProd}
            onFileAction={handleFileAction}
          />
        )}
      </InfiniteScroll>
    );
  };

  return (
    <Card>
      <div style={{ marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <Title level={4} style={{ margin: 0 }}>
          <Space>
            <FileOutlined />
            Allegati
            {totalCount > 0 && (
              <Text type="secondary" style={{ fontSize: "16px", fontWeight: "normal" }}>
                ({totalCount} totali)
              </Text>
            )}
          </Space>
        </Title>

        <Space>
          <Segmented
            options={viewOptions}
            value={isGridView ? "grid" : "table"}
            onChange={setViewMode}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
          >
            Aggiorna
          </Button>
        </Space>
      </div>

      {renderContent()}

      <Modal
        visible={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="90vw"
        style={{ top: 20 }}
        bodyStyle={{
          maxHeight: "calc(100vh - 200px)",
          overflow: "auto",
          padding: "20px",
        }}
      >
        <div style={{
          textAlign: "center",
          padding: "20px",
          background: "repeating-conic-gradient(#f9f9f9 0% 25%, #ffffff 0% 50%) 50% / 20px 20px",
          borderRadius: "4px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}>
          <Image
            src={previewImage}
            style={{
              maxWidth: "100%",
              maxHeight: "calc(100vh - 350px)",
              objectFit: "contain",
            }}
            preview={{
              visible: false,
              mask: null,
            }}
          />
        </div>
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <Space>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => {
                const link = document.createElement("a");
                link.href = previewImage;
                link.download = previewTitle;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              Scarica
            </Button>
            <Button
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(previewImage).then(() => {
                  message.success("URL copiato negli appunti!");
                }).catch(() => {
                  message.error("Impossibile copiare l'URL");
                });
              }}
            >
              Copia URL
            </Button>
            <Button
              icon={<EyeOutlined />}
              onClick={() => window.open(previewImage, "_blank")}
            >
              Apri in nuova scheda
            </Button>
          </Space>
        </div>
      </Modal>
    </Card>
  );
};

export default OrgAllegati;
